import { Button, Collapse, Input, Typography, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import BeforeAfterPreview from '../../../components/BeforeAfterPreview';
import InputParameter from '../../../components/InputParameter';
import colors from '../../../utils/theme/colors';
import _ from 'lodash';
import JsonBeautifyTab from '../../app/playgroundScreen/components/JsonBeautifyTab';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';

// WorkflowConfigCol allows configuring parameters for a selected workflow, similar to ModelConfigCol
function WorkflowConfigCol({
  handleRunWorkflow,
  selectedWorkflow,
  fetchWorkflow,
  workflowsState,
  fetchWorkflows,
  onWorkflowSelect,
}) {
  const [submitForm, setSubmitForm] = useState({
    workflow_definition_id: null,
    params: {},
    available_params: [],
    lastWorkflowParams: {},
  });

  // Developer / picker states
  const [activeTab, setActiveTab] = useState('basic');
  const [jsonEdit, setJsonEdit] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(!selectedWorkflow?.workflow_definition_id);
  const [searchValue, setSearchValue] = useState(
    workflowsState?.params?.name || workflowsState?.params?.workflow_name || ''
  );
  const debouncedFetchRef = useRef(
    _.debounce((name, baseParams, fetch) => {
      if (fetch) {
        fetch({ ...baseParams, name });
      }
    }, 300)
  );

  useEffect(() => {
    const debounced = debouncedFetchRef.current;
    return () => {
      if (debounced && typeof debounced.cancel === 'function') {
        debounced.cancel();
      }
    };
  }, []);

  // Hover state for slider activation
  const [hoveredWorkflowId, setHoveredWorkflowId] = useState(null);

  // Grid card renderer (component scope)
  const renderWorkflowGridCard = (item) => {
    const isSelected = selectedWorkflow?.workflow_definition_id === item.workflow_definition_id;
    const isHovered = hoveredWorkflowId === item.workflow_definition_id;
    return (
      <div
        key={item.workflow_definition_id}
        onClick={() => {
          onWorkflowSelect && onWorkflowSelect(item);
          setIsPickerOpen(false);
        }}
        style={{
          cursor: 'pointer',
          background: isSelected
            ? 'linear-gradient(135deg, rgba(24, 119, 242, 0.15), rgba(114, 46, 209, 0.1))'
            : 'rgba(255,255,255,0.03)',
          borderRadius: 12,
          padding: 10,
          transition: 'all 0.2s ease',
          border: isSelected
            ? '2px solid rgba(24, 119, 242, 0.4)'
            : '1px solid rgba(255,255,255,0.08)'
        }}
        onMouseEnter={(e) => {
          setHoveredWorkflowId(item.workflow_definition_id);
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
          }
        }}
        onMouseLeave={(e) => {
          setHoveredWorkflowId(null);
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        {/* Media area 3:2 */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3 / 2',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          {(item.workflow_cover_image_url && item.workflow_cover_2_image_url) ? (
            isHovered ? (
              <BeforeAfterPreview
                before={item.workflow_cover_image_url}
                after={item.workflow_cover_2_image_url}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  transition: 'transform 0.25s ease, filter 0.25s ease',
                  filter: isSelected ? 'none' : 'saturate(1)'
                }}
                className=''
              />
            ) : (
              <img
                src={item.workflow_cover_2_image_url}
                alt={item.name || item.workflow_name}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: isSelected ? 'none' : 'saturate(1)',
                  transition: 'transform 0.25s ease, filter 0.25s ease'
                }}
              />
            )
          ) : (
            <img
              src={
                item.workflow_cover_image_url ||
                item.workflow_cover_2_image_url ||
                'https://png.pngtree.com/png-vector/20190820/ourmid/pngtree-no-image-vector-illustration-isolated-png-image_1694547.jpg'
              }
              alt={item.name || item.workflow_name}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: isSelected ? 'none' : 'saturate(1)',
                transition: 'transform 0.25s ease, filter 0.25s ease'
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 56,
              background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))'
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.foreground,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={item.name || item.workflow_name}
              >
                {item.name || item.workflow_name}
              </span>
            </div>
            {item.description && (
              <div
                style={{
                  fontSize: 11,
                  color: colors.muted_foreground,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={item.description}
              >
                {item.description}
              </div>
            )}
            {item.version && (
              <div style={{ fontSize: 11, color: colors.muted_foreground, marginTop: 6 }}>v{item.version}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Fetch workflow details and params when selectedWorkflow changes
  useEffect(() => {
    if (selectedWorkflow?.workflow_definition_id) {
      setSubmitForm((prev) => ({
        ...prev,
        workflow_definition_id: selectedWorkflow.workflow_definition_id,
      }));
      
      fetchWorkflow(selectedWorkflow.workflow_definition_id).then((w) => {
        // Debug log to check what is returned for the workflow
        // eslint-disable-next-line no-console
        console.log('Fetched workflow:', w);
        // Assume w.parameters is an array of parameter configs
        setSubmitForm((prev) => {
          const defaults = {};
          (w.parameters || []).forEach((param) => {
            defaults[param.parameter_name] = param.default_value;
          });
          // Try to load persisted params for this workflow
          let persisted = null;
          try {
            const key = `wf_params_${selectedWorkflow.workflow_definition_id}`;
            const raw = localStorage.getItem(key);
            if (raw) persisted = JSON.parse(raw);
          } catch (_) {}
          const params = persisted && typeof persisted === 'object' ? { ...defaults, ...persisted } : defaults;
          return {
            ...prev,
            available_params: w.parameters || [],
            params,
            lastWorkflowParams: defaults,
          };
        });
      });
    } else {
      // Reset form when no workflow is selected
      setSubmitForm({
        workflow_definition_id: null,
        params: {},
        available_params: [],
        lastWorkflowParams: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkflow]);

  // Auto-open picker if no workflow selected
  useEffect(() => {
    if (!selectedWorkflow?.workflow_definition_id) {
      setIsPickerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkflow?.workflow_definition_id]);

  // When overlay opens, trigger a fresh fetch using current filters and sync search input
  useEffect(() => {
    if (!isPickerOpen) return;
    const initialName = workflowsState?.params?.name || workflowsState?.params?.workflow_name || '';
    setSearchValue((prev) => (prev !== '' ? prev : initialName));
    if (fetchWorkflows) {
      fetchWorkflows({
        ...workflowsState?.params,
        name: (searchValue || initialName || '').trim(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickerOpen]);

  // Sync JSON when form changes
  useEffect(() => {
    if (activeTab === 'json') {
      setJsonEdit(
        JSON.stringify(
          {
            workflow_definition_id: submitForm.workflow_definition_id,
            params: submitForm.params,
          },
          null,
          2
        )
      );
    }
  }, [submitForm.workflow_definition_id, submitForm.params, activeTab]);



  // Handle param input change
  const handleParamChange = (name, value) => {
    setSubmitForm((prev) => {
      const next = { ...prev, params: { ...prev.params, [name]: value } };
      // Persist after each change
      try {
        if (next.workflow_definition_id) {
          const key = `wf_params_${next.workflow_definition_id}`;
          localStorage.setItem(key, JSON.stringify(next.params));
        }
      } catch (_) {}
      return next;
    });
  };

  // Helpers: missing required params
  function isEmptyValue(param, value) {
    if (value === null || value === undefined) return true;
    switch (param.data_type) {
      case 'string':
        return String(value).trim() === '';
      case 'number':
      case 'float':
        return value === '' || Number.isNaN(Number(value));
      case 'boolean':
        return value === undefined || value === null;
      case 'object[]':
        return !Array.isArray(value) || value.length === 0;
      case 'object':
        if (typeof value === 'string') {
          try {
            JSON.parse(value);
            return false;
          } catch {
            return String(value).trim() === '';
          }
        }
        return typeof value !== 'object' || value === null;
      default:
        return false;
    }
  }

  function getMissingRequiredParams() {
    const paramsList = submitForm.available_params || [];
    return paramsList
      .filter((p) => Number(p.is_required) === 1 || p.is_required === true || Number(p.required) === 1 || p.required === true)
      .filter((p) => isEmptyValue(p, submitForm.params[p.parameter_name]));
  }

  // JSON handlers
  const handleCopyJson = () => {
    navigator.clipboard.writeText(jsonEdit);
    message.success('Copied JSON to clipboard');
  };

  // Removed JSON textarea editing handler (read-only dev view for now)

  // UI fragments for embedded workflow picker
  const renderWorkflowSummary = () => {
    if (!selectedWorkflow?.workflow_definition_id) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          />
          <div style={{ color: colors.foreground, fontSize: 14, fontWeight: 700 }}>Select a workflow</div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <img
            src={selectedWorkflow.workflow_cover_2_image_url || selectedWorkflow.workflow_cover_image_url}
            alt={selectedWorkflow.workflow_name || selectedWorkflow.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 6
            }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            color: colors.foreground,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={selectedWorkflow.workflow_name || selectedWorkflow.name}>
            {selectedWorkflow.workflow_name || selectedWorkflow.name}
          </div>
          {selectedWorkflow.description && (
            <div style={{
              marginTop: 6,
              fontSize: 11,
              color: colors.muted_foreground,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 260
            }} title={selectedWorkflow.description}>
              {selectedWorkflow.description}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Run workflow
  const onRunWorkflow = () => {
    // Guard: prevent run if any required params are missing
    const missing = getMissingRequiredParams();
    if (missing.length) {
      const names = missing
        .map((p) => p.parameter_title || p.parameter_name)
        .join(', ');
      message.warning(`Please fill required parameter(s): ${names}`);
      return;
    }
    handleRunWorkflow({
      workflow_definition_id: submitForm.workflow_definition_id,
      params: submitForm.params,
    });
  };

  // Reset to defaults
  const onResetDefaults = () => {
    setSubmitForm((prev) => {
      const next = {
        ...prev,
        params: { ...(prev.lastWorkflowParams || {}) },
      };
      try {
        if (next.workflow_definition_id) {
          const key = `wf_params_${next.workflow_definition_id}`;
          localStorage.setItem(key, JSON.stringify(next.params));
        }
      } catch (_) {}
      return next;
    });
  };

  // Simple parameter rendering using shared InputParameter component
  const renderParameter = (param) => {
    const value = submitForm.params[param.parameter_name];
    
    return (
      <InputParameter
        key={param.parameter_name}
        param={param}
        value={value}
        onChange={(val) => handleParamChange(param.parameter_name, val)}
      />
    );
  };

  return (
    <div
      style={{
        padding: 0,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
    {/* Header */}
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{
          fontSize: 24,
          background: 'linear-gradient(135deg, #1677ff, #722ed1)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>🧩</div>
        <Typography.Text style={{
          color: colors.foreground,
          fontSize: 20,
          fontWeight: 700,
          background: 'linear-gradient(135deg, #ffffff, #e6e6e6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Workflow
        </Typography.Text>
      </div>
      <Typography.Text style={{ color: colors.muted_foreground, fontSize: 14, display: 'block' }}>
        {selectedWorkflow?.workflow_name || selectedWorkflow?.name || 'Select a workflow to start'}
      </Typography.Text>
    </div>

    {/* Picker summary + expand */}
    <div style={{ marginBottom: 12 }}>
      <div
        onClick={() => setIsPickerOpen((v) => !v)}
        style={{
          padding: !selectedWorkflow?.workflow_definition_id ? 2 : 0,
          borderRadius: 12,
          background: !selectedWorkflow?.workflow_definition_id
            ? 'linear-gradient(135deg, rgba(22, 119, 242, 0.8), rgba(114, 46, 209, 0.8))'
            : 'transparent',
          boxShadow: !selectedWorkflow?.workflow_definition_id ? '0 6px 24px rgba(114,46,209,0.25)' : 'none',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s ease, background 0.2s ease'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          {renderWorkflowSummary()}
          <div style={{ color: colors.muted_foreground, fontSize: 12, marginLeft: 8 }}>
            {isPickerOpen ? '▲' : '▼'}
          </div>
        </div>
      </div>
    </div>

    {/* Full overlay workflow selector */}
    {isPickerOpen && (
      <div
        className='workflow-selector-overlay'
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 4000,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)'
        }}
      >
        {/* Overlay header with search and close */}
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(0,0,0,0.35)'
          }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.foreground,
                whiteSpace: 'nowrap'
              }}
            >
              Select a Workflow
            </div>
            <Input
              placeholder='🔍 Search workflows...'
              allowClear
              value={searchValue}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
                // debounce API to avoid spamming and race conditions
                debouncedFetchRef.current(
                  val,
                  workflowsState?.params,
                  fetchWorkflows
                );
              }}
              suffix={workflowsState?.loading ? <LoadingOutlined spin /> : <SearchOutlined />}
              style={{ flex: 1, height: 40 }}
            />
            <Button onClick={() => setIsPickerOpen(false)} style={{ height: 40 }}>
              Close
            </Button>
          </div>
        </div>

        {/* Overlay grid */}
        <div className='no-scrollbar' style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12
            }}
          >
            {(workflowsState?.data || []).map((wf) => renderWorkflowGridCard(wf))}
          </div>
          {!workflowsState?.loading && (workflowsState?.data || []).length === 0 && (
            <div style={{ color: colors.muted_foreground, fontSize: 12, padding: 8 }}>
              No workflows found
            </div>
          )}
        </div>
      </div>
    )}

    {/* Developer Mode Toggle */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <button
        onClick={() => setActiveTab(activeTab === 'json' ? 'basic' : 'json')}
        style={{
          background: 'none',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 6,
          padding: '4px 8px',
          color: colors.muted_foreground,
          fontSize: 11,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => {
          e.target.style.borderColor = 'rgba(255,255,255,0.2)';
          e.target.style.color = colors.foreground;
        }}
        onMouseLeave={(e) => {
          e.target.style.borderColor = 'rgba(255,255,255,0.1)';
          e.target.style.color = colors.muted_foreground;
        }}
      >
        {activeTab === 'json' ? '← Back' : '{ } Developer'}
      </button>
    </div>

    {/* Params or JSON */}
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {activeTab === 'json' ? (
        <div style={{ marginTop: 16 }}>
          <Typography.Text style={{ color: colors.muted_foreground, fontSize: 13, display: 'block', marginBottom: 16 }}>
            Raw JSON parameters for debugging and advanced use
          </Typography.Text>
          <JsonBeautifyTab data={submitForm.params} />
          <Button onClick={handleCopyJson} style={{ marginTop: 12 }}>Copy JSON</Button>
        </div>
      ) : selectedWorkflow?.workflow_definition_id ? (
        <Collapse
          style={{ background: '#0b0b0c', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}
          defaultActiveKey={['params']}
          items={[
            {
              key: 'params',
              label: 'Parameters',
              children: (
                <>
                  {submitForm.available_params.length === 0 && (
                    <Typography.Text type='secondary' style={{ padding: 16 }}>
                      No parameters
                    </Typography.Text>
                  )}
                  {submitForm.available_params.map((param) => (
                    <React.Fragment key={param.parameter_name}>
                      {renderParameter(param)}
                    </React.Fragment>
                  ))}
                </>
              ),
            },
          ]}
        />
      ) : (
        <div
          style={{
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center'
          }}
        >
          <Typography.Text type='secondary'>Select a workflow to configure its parameters</Typography.Text>
        </div>
      )}
    </div>
    {/* Sticky footer actions */}
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.25))',
        paddingTop: 10,
        paddingBottom: 8,
        borderTop: '1px solid rgba(255,255,255,0.06)'
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          position: 'relative',
          zIndex: 1,
          width: '100%',
        }}
      >
        <Button 
          onClick={onResetDefaults} 
          disabled={!submitForm.workflow_definition_id}
          type='text'
          style={{
            flex: 0.2,
            color: '#888',
            fontSize: '12px',
            padding: '4px 8px',
            height: 'auto',
            border: 'none',
            background: 'transparent'
          }}
        >
          Reset
        </Button>
        <div style={{ position: 'relative', flex: 1, padding: 2 }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 8,
              padding: 2,
              background: `linear-gradient(
                45deg,
                #ff6b6b,
                #4ecdc4,
                #45b7d1,
                #96ceb4,
                #feca57,
                #ff9ff3,
                #54a0ff,
                #5f27cd,
                #ff6b6b
              )`,
              backgroundSize: '400% 400%',
              zIndex: -1,
              animation: 'gradientFlow 4s ease-in-out infinite',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'xor',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
            }}
          />
          <Button
            type='primary'
            block
            style={{
              background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d, #1a1a1a)',
              color: 'white',
              border: 'none',
              position: 'relative',
              zIndex: 1,
              fontWeight: 600,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
            }}
            onClick={onRunWorkflow}
            disabled={!submitForm.workflow_definition_id || getMissingRequiredParams().length > 0}
          >
            Run Workflow
          </Button>
        </div>
      </div>
    </div>
  </div>
);

}
export default WorkflowConfigCol;
